import React, { ReactElement } from 'react'

import { useDebouncedCallback } from 'use-debounce'
import styled, { css } from 'styled-components'

import { variables } from 'src/Styled/variables'
import Loader from 'src/Assets/Svg/loader.svg?react'
import { DEBOUNCE_DELAY_100 } from 'src/Constants/constants'

export interface CTAButtonProps extends React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> {
  icon?: React.ReactNode
  loading?: boolean
}

export const ctaButtonStyles = css<{ $loading?: boolean }>`
  background: ${({ theme }) => theme.color.black};
  border: ${({ theme }) => `1px solid ${theme.color.black}`};
  color: ${({ theme }) => theme.color.white};
  cursor: pointer;
  transition: all 0.2s;

  svg {
    path {
      fill: ${({ theme }) => theme.color.white};
    }
  }

  &:hover {
    background: ${({ theme }) => theme.color.hoverBlack};
    border-color: ${({ theme }) => theme.color.hoverBlack};
  }

  &:focus-visible {
    outline: none;
    background: ${({ theme }) => theme.color.black};
    border-color: ${({ theme }) => theme.color.brandGreen};
    color: ${({ theme }) => theme.color.brandGreen};

    svg {
      path {
        fill: ${({ theme }) => theme.color.brandGreen};
      }
    }
  }

  &:active {
    outline: none;
    background: ${({ theme }) => theme.color.black};
    border-color: ${({ theme }) => theme.color.black};
    color: ${({ theme }) => theme.color.darkGrey};

    svg {
      path {
        fill: ${({ theme }) => theme.color.darkGrey};
      }
    }
  }

  ${({ $loading }) => {
    if (!$loading) {
      return css`
        &:disabled {
          cursor: not-allowed;
          background: ${({ theme }) => theme.color.hoverWhite};
          border-color: ${({ theme }) => theme.color.hoverWhite};
          color: ${({ theme }) => theme.color.lightGrey};

          svg {
            path {
              fill: ${({ theme }) => theme.color.lightGrey};
            }
          }
        }
      `
    }

    return css`
      &,
      &:hover {
        background: ${({ theme }) => theme.color.black};
        border-color: ${({ theme }) => theme.color.black};
      }

      svg {
        path {
          fill: ${({ theme }) => theme.color.mediumGrey};
        }
      }
    `
  }}
`

export const StyledButton = styled.button<{
  $loading?: boolean
}>`
  position: relative;
  display: flex;
  height: 48px;
  padding: 8px 40px;
  justify-content: center;
  align-items: center;
  border-radius: 40px;
  gap: 8px;
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight600};
  line-height: 150%;

  ${ctaButtonStyles}
`

const LoaderWrapper = styled.div`
  position: absolute;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  svg {
    width: 17px;
    height: 17px;
  }
`

const CTAChildrenWrapper = styled.div<{ $loading?: boolean }>`
  opacity: ${({ $loading }) => ($loading ? 0 : 1)};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`

export const CTAButton: React.FC<CTAButtonProps> = (props): ReactElement => {
  const { children, loading, onClick, disabled, icon, ...restProps } = props

  const debouncedOnClick = useDebouncedCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (typeof onClick === 'function') {
        onClick(event)
      }
    },
    DEBOUNCE_DELAY_100,
  )
  return (
    <StyledButton
      {...restProps}
      $loading={loading && !disabled}
      onClick={(e) => debouncedOnClick(e)}
      disabled={disabled || loading}
    >
      {loading && !disabled && (
        <LoaderWrapper>
          <Loader data-testid='loader' />
        </LoaderWrapper>
      )}

      {Boolean(icon) && !loading && icon}
      <CTAChildrenWrapper $loading={loading}>{children}</CTAChildrenWrapper>
    </StyledButton>
  )
}
